import htmx from "htmx.org";

/** The context htmx events carry in `event.detail.ctx`, as far as we use it. */
export interface HtmxRequestContext {
  request: { method: string; headers: Record<string, string> };
}

export interface HtmxResponseContext extends HtmxRequestContext {
  text: string;
  response: { status: number; headers: Headers };
}

export const requestContext = (event: Event): HtmxRequestContext | null => {
  return (
    (event as CustomEvent<{ ctx?: HtmxRequestContext }>).detail?.ctx ?? null
  );
};

/**
 * Replace the children of `target` with those of the same element at `url`,
 * and show `historyUrl` in the address bar.
 */
export const swapChildren = async (
  target: HTMLElement,
  url: string,
  historyUrl: string,
): Promise<void> => {
  await htmx.ajax("GET", url, {
    target,
    select: `#${target.id} > *`,
    swap: "innerHTML",
    replace: historyUrl,
  });
};

/** Run the callback on the initial page load and all partial loads by htmx. */
export const onLoad = (callback: (element: Element) => void): void => {
  htmx.onLoad(callback);
};

/**
 * Show Django's debug error page. Its CSS is in <head>, so htmx's body swap
 * would render it unstyled.
 */
export const replaceDocument = (page: Document): void => {
  document.documentElement.replaceWith(page.documentElement);
};

if (import.meta.env.DEV) {
  // Failed partial requests show a toast that opens the error page, see
  // toasts.ts.
  (htmx.config.noSwap as (number | string)[]).push("4xx", "5xx");
  document.addEventListener("htmx:response:error", (event) => {
    const { ctx } = (event as CustomEvent<{ ctx: HtmxResponseContext }>).detail;
    const headers = ctx.response.headers;
    if (
      ctx.request.headers["HX-Boosted"] !== "true" ||
      headers.get("HX-Redirect") !== null ||
      headers.get("HX-Refresh") !== null
    ) {
      return;
    }
    replaceDocument(new DOMParser().parseFromString(ctx.text, "text/html"));
  });
}
